import React, { useCallback, MouseEvent, ButtonHTMLAttributes } from 'react';
import { Box, Text } from '@design-system/pc';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { getAsset } from '@/utils';
import LoadImage from '@/components/LoadImage';

interface SeqConsultOptionButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  isActive?: boolean;
  variant?: 'default' | 'photo';
  imgSrc?: string;
}

const SeqConsultOptionButton = ({
  children,
  isActive,
  variant = 'default',
  imgSrc: imageSrc,
  ...props
}: SeqConsultOptionButtonProps) => {
  const onToggle = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.classList.toggle('active');
  }, []);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onToggle(e);
      props.onClick?.(e);
    },
    [onToggle, props],
  );

  if (variant === 'photo') {
    return (
      <PhotoButton
        data-cypress-element
        {...props}
        onClick={handleClick}
        className={classNames({ active: isActive })}
      >
        <Box className="seq-scope-item-photo">
          {imageSrc && (
            <LoadImage
              style={{
                width: 'auto',
                maxWidth: 'initial',
                flexShrink: '0',
                flexGrow: '1',
              }}
              src={getAsset(imageSrc)}
            />
          )}
        </Box>

        <Box className="seq-scope-item-title">
          <Text variant="subTitle6" color="color_chip.primary">
            {children}
          </Text>
        </Box>
      </PhotoButton>
    );
  }

  return (
    <OptionButton
      data-cypress-element
      {...props}
      onClick={handleClick}
      className={classNames({ active: isActive })}
    >
      <Text variant="subTitle6" color="color_chip.primary">
        {children}
      </Text>
    </OptionButton>
  );
};

export default SeqConsultOptionButton;

const OptionButton = styled.button`
  ${({ theme }) => css`
    cursor: pointer;
    border: 0;
    min-width: 68px;
    padding: 23px 20px;
    border-radius: 100px;
    width: 100%;
    height: 68px;
    border: 1px solid ${theme.colors.color_chip.gray[50]};
    background-color: ${theme.colors.color_chip.gray[50]};

    &:hover {
      border: 1px solid ${theme.colors.color_chip.gray[700]};
    }

    &.active {
      & > div {
        color: ${theme.colors.color_chip.blue[500]} !important;
      }

      border: 1px solid ${theme.colors.color_chip.blue[500]};
      background-color: rgba(0, 100, 255, 0.1);
    }
  `};
`;

const PhotoButton = styled.button`
  ${({ theme }) => css`
    cursor: pointer;
    border: 0;
    outline: none;
    background-color: transparent;
    height: 168px;
    position: relative;

    &::after {
      content: '';
      display: none;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 1px solid ${theme.colors.color_chip.gray[700]};
    }

    &:hover {
      &::after {
        display: block;
      }
    }

    .seq-scope-item-photo {
      display: flex;
      justify-content: center;
      height: 128px;
      overflow: hidden;
      background-color: ${theme.colors.color_chip.gray[50]};
    }

    .seq-scope-item-title {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
    }

    &.active {
      &::after {
        display: block;
        border: 1px solid ${theme.colors.color_chip.blue[500]};
      }

      .seq-scope-item-title {
        & > div {
          color: ${theme.colors.color_chip.blue[500]} !important;
        }
        background-color: rgba(0, 100, 255, 0.1);
      }
    }
  `};
`;
